import React, { useEffect, useRef } from 'react';
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { normalizeMentionTextAnchors } from './editor';
import {
  $createMentionNode,
  $isMentionNode,
} from './nodes';
import { MentionOption } from './types';

export type MentionMenuState = {
  query: string;
  nodeKey: string;
  startOffset: number;
  endOffset: number;
  top: number;
  left: number;
};

interface SlashTriggerKeyboardEvent {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
}

/** 只有未带系统修饰键的物理 Slash 键可以授权首次打开技能候选。 */
export function isDirectSlashTriggerKey(event: SlashTriggerKeyboardEvent) {
  return event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey;
}

/** 获取输入联想触发词的匹配规则，@ 同时兼容半角和全角字符。 */
export const getTriggerMatchRegExp = (triggerChar: '@' | '/') => {
  if (triggerChar === '/') {
    return /\/([^\s/]*)$/;
  }
  return /[@＠]([^\s@＠]*)$/;
};

/**
 * 判断光标是否只是移入已有触发词中间，避免重新打开候选弹框。
 */
export function shouldSuppressTriggerMatch({
  triggerChar,
  query,
  text,
  offset,
  allowEmptyQueryBeforeText = false,
}: {
  triggerChar: '@' | '/';
  query: string;
  text: string;
  offset: number;
  allowEmptyQueryBeforeText?: boolean;
}) {
  if (allowEmptyQueryBeforeText) return false;
  const nextChar = text[offset] || '';
  const isSameTriggerChar = triggerChar === '@'
    ? nextChar === '@' || nextChar === '＠'
    : nextChar === triggerChar;
  if (!nextChar || /\s/.test(nextChar) || isSameTriggerChar) return false;
  return query.length === 0;
}

/**
 * 监听 Lexical 光标和文本变化，并把触发字符命中的弹框锚点同步给外层控制器。
 */
function TriggerMenuPlugin({
  triggerChar,
  hasOptions,
  alwaysTrigger = false,
  allowEmptyQueryBeforeText = false,
  editorRootRef,
  onMatchChange,
}: {
  triggerChar: '@' | '/';
  hasOptions: boolean;
  alwaysTrigger?: boolean;
  allowEmptyQueryBeforeText?: boolean;
  editorRootRef: React.RefObject<HTMLDivElement>;
  onMatchChange: (nextState: MentionMenuState | null) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const hasOptionsRef = useRef(hasOptions);
  hasOptionsRef.current = hasOptions;
  const alwaysTriggerRef = useRef(alwaysTrigger);
  alwaysTriggerRef.current = alwaysTrigger;
  const allowEmptyQueryBeforeTextRef = useRef(allowEmptyQueryBeforeText);
  allowEmptyQueryBeforeTextRef.current = allowEmptyQueryBeforeText;
  const onMatchChangeRef = useRef(onMatchChange);
  onMatchChangeRef.current = onMatchChange;
  const updateMatchStateRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    /** 根据触发字符在编辑器可视区域内的位置刷新候选弹框锚点。 */
    const updateMatchState = () => {
      if (!hasOptionsRef.current && !alwaysTriggerRef.current) {
        onMatchChangeRef.current(null);
        return;
      }

      let nextState: MentionMenuState | null = null;

      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return;

        const anchorNode = selection.anchor.getNode();
        if (!$isTextNode(anchorNode)) return;

        const text = anchorNode.getTextContent();
        const textBefore = text.slice(0, selection.anchor.offset);
        const match = textBefore.match(getTriggerMatchRegExp(triggerChar));
        if (!match) return;

        const query = match[1] || '';
        if (shouldSuppressTriggerMatch({
          triggerChar,
          query,
          text,
          offset: selection.anchor.offset,
          allowEmptyQueryBeforeText: allowEmptyQueryBeforeTextRef.current,
        })) return;
        const startOffset = textBefore.length - query.length - 1;
        const domSelection = window.getSelection();
        const domRange = domSelection && domSelection.rangeCount > 0 ? domSelection.getRangeAt(0) : null;
        const rootElement = editorRootRef.current;
        if (!domRange || !rootElement) return;

        const triggerRange = domRange.cloneRange();
        const {
          startContainer,
        } = triggerRange;

        if (startContainer.nodeType === Node.TEXT_NODE) {
          const textLength = startContainer.textContent?.length || 0;
          const triggerOffset = Math.min(startOffset, textLength);
          const triggerEndOffset = Math.min(startOffset + 1, textLength);

          if (triggerOffset < triggerEndOffset) {
            triggerRange.setStart(startContainer, triggerOffset);
            triggerRange.setEnd(startContainer, triggerEndOffset);
          }
        }

        const rect = triggerRange.getClientRects()[0] || triggerRange.getBoundingClientRect() || domRange.getBoundingClientRect();
        const rootRect = rootElement.getBoundingClientRect();

        nextState = {
          query,
          nodeKey: anchorNode.getKey(),
          startOffset,
          endOffset: selection.anchor.offset,
          top: rect.top - rootRect.top - 6,
          left: rect.left - rootRect.left,
        };
      });

      onMatchChangeRef.current(nextState);
    };

    updateMatchStateRef.current = updateMatchState;

    updateMatchState();

    const unregisterSelectionChange = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateMatchState();
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );

    const unregisterTextContent = editor.registerTextContentListener(() => {
      updateMatchState();
    });

    const rootElement = editorRootRef.current;
    rootElement?.addEventListener('scroll', updateMatchState, { passive: true });

    return () => {
      updateMatchStateRef.current = null;
      unregisterSelectionChange();
      unregisterTextContent();
      rootElement?.removeEventListener('scroll', updateMatchState);
    };
  }, [editor, editorRootRef, triggerChar]);

  useEffect(() => {
    updateMatchStateRef.current?.();
  }, [hasOptions]);

  return null;
}

/**
 * 监听 @ 触发词并同步候选弹框锚点。
 */
export function MentionTriggerPlugin({
  mentionOptions,
  alwaysTrigger = false,
  allowEmptyQueryBeforeText = false,
  editorRootRef,
  onMatchChange,
}: {
  mentionOptions: MentionOption[];
  alwaysTrigger?: boolean;
  allowEmptyQueryBeforeText?: boolean;
  editorRootRef: React.RefObject<HTMLDivElement>;
  onMatchChange: (nextState: MentionMenuState | null) => void;
}) {
  return (
    <TriggerMenuPlugin
      triggerChar="@"
      hasOptions={mentionOptions.length > 0}
      alwaysTrigger={alwaysTrigger}
      allowEmptyQueryBeforeText={allowEmptyQueryBeforeText}
      editorRootRef={editorRootRef}
      onMatchChange={onMatchChange}
    />
  );
}

/** 监听 / 触发词并同步技能候选弹框锚点。 */
export function SlashTriggerPlugin({
  skillOptions,
  editorRootRef,
  onMatchChange,
}: {
  skillOptions: MentionOption[];
  editorRootRef: React.RefObject<HTMLDivElement>;
  onMatchChange: (nextState: MentionMenuState | null) => void;
}) {
  return (
    <TriggerMenuPlugin
      triggerChar="/"
      hasOptions={skillOptions.length > 0}
      allowEmptyQueryBeforeText
      editorRootRef={editorRootRef}
      onMatchChange={onMatchChange}
    />
  );
}

// MentionNode 现在是 DecoratorNode，Lexical 原生的 NodeSelection 机制
// 会自动处理键盘删除，无需自定义 MentionDeletePlugin

/** 提及替换完成后把光标放到提及节点后的零宽锚点之后，避免 Safari 把 caret 画进 @ pill 内部。 */
export const insertMentionNode = ({
  editor,
  option,
  triggerState,
}: {
  editor: LexicalEditor;
  option: MentionOption;
  triggerState: MentionMenuState;
}) => {
  editor.focus();
  editor.update(() => {
    const node = $getNodeByKey(triggerState.nodeKey);
    if (!$isTextNode(node) || $isMentionNode(node)) return;

    const splitNodes = node.splitText(triggerState.startOffset, triggerState.endOffset);
    const mentionTextNode = triggerState.startOffset === 0
      ? splitNodes[0]
      : splitNodes[1];
    if (!mentionTextNode || !$isTextNode(mentionTextNode)) return;

    const mentionNode = $createMentionNode({
      id: option.id,
      name: option.name,
    });
    mentionTextNode.replace(mentionNode);
    normalizeMentionTextAnchors();

    const nextSibling = mentionNode.getNextSibling();
    if ($isTextNode(nextSibling)) {
      const startOffset = getTextNodeCaretStartOffset(nextSibling);
      nextSibling.select(startOffset, startOffset);
    }
  });
};
